import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Lead } from '@otw/models/leads/lead';
import { Vehicle } from '@otw/models/inventory/inventory';
import { formatPrice, formatDistance } from 'core/utils/inventoryUtils';
import { format } from 'date-fns';
import { Theme } from '@mui/material';
import { RETAIL_ORDER_DOC_FEE } from '@/components/inventory/actions/saleInvoiceTypes'

// Generate PDF for suggested trucks
export const generateSuggestedTrucksPDF = (
  lead: Lead, 
  suggestedVehicles: any[], 
  theme: Theme, 
  returnDoc = false
) => {
  if (!suggestedVehicles.length) return null;
  
  // Create a new PDF document
  const doc = new jsPDF();
  
  // Get primary color from theme (converting to RGB array for jsPDF)
  const primaryColorHex = theme.palette.primary.main.replace('#', '');
  const primaryColorRGB = [
    parseInt(primaryColorHex.substring(0, 2), 16),
    parseInt(primaryColorHex.substring(2, 4), 16),
    parseInt(primaryColorHex.substring(4, 6), 16)
  ];
  
  // Add title with primary color
  doc.setFontSize(16);
  doc.setTextColor(primaryColorRGB[0], primaryColorRGB[1], primaryColorRGB[2]);
  doc.text(`Suggested Trucks for ${lead.customerName || 'Lead'}`, 14, 15);
  
  // Add lead info with primary color
  doc.setFontSize(10);
  doc.setTextColor(primaryColorRGB[0], primaryColorRGB[1], primaryColorRGB[2]);
  doc.text(`Generated on: ${format(new Date(), 'MM/dd/yyyy')}`, 14, 22);
  if (lead.company) {
    doc.text(`Company: ${lead.company}`, 14, 27);
  }
  
  // Prepare table data
  const tableColumn = [
    "Type", 
    "Year/Make/Model", 
    "Stock #",
    "Size", 
    "Mileage", 
    "Condition", 
    "Price", 
    "Match"
  ];
  
  const tableRows = suggestedVehicles.map(vehicle => [
    vehicle.type || 'N/A',
    `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'N/A',
    vehicle.stock_number || 'N/A',
    vehicle.size || 'N/A',
    vehicle.miles ? formatDistance(vehicle.miles) : 'N/A',
    vehicle.condition || 'N/A',
    vehicle.optic_list_price ? formatPrice(vehicle.optic_list_price) : 'N/A',
    vehicle.similarityLevel === 'exact' ? 'Exact Match' : 'Similar Match'
  ]);
  
  // Add the table
  (doc as any).autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 32,
    styles: { fontSize: 8 },
    headStyles: { 
      fillColor: primaryColorRGB,
      textColor: [255, 255, 255]
    },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    rowStyles: (rowIndex: number) => {
      const vehicle = suggestedVehicles[rowIndex];
      if (vehicle.similarityLevel === 'exact') {
        return { textColor: [0, 128, 0] };
      }
      return {};
    },
    didParseCell: (data: any) => {
      if (data.section === 'body' && !data.cell.raw.includes('Exact Match')) {
        data.cell.styles.textColor = primaryColorRGB;
      }
      else if (data.section === 'body'){
        data.cell.styles.textColor = [0, 128, 0];
      }
    }
  });
  
  if (returnDoc) {
    return doc;
  } else {
    doc.save(`Suggested Trucks for ${lead.customerName}.pdf`);
    return null;
  }
};

// Generate PDF for linked vehicles
export const generateLinkedVehiclesPDF = (
  lead: Lead, 
  linkedVehicles: Vehicle[], 
  theme: Theme, 
  returnDoc = false
) => {
  if (!linkedVehicles.length) return null;
  
  // Create a new PDF document
  const doc = new jsPDF();
  
  // Get primary color from theme (converting to RGB array for jsPDF)
  const primaryColorHex = theme.palette.primary.main.replace('#', '');
  const primaryColorRGB = [
    parseInt(primaryColorHex.substring(0, 2), 16),
    parseInt(primaryColorHex.substring(2, 4), 16),
    parseInt(primaryColorHex.substring(4, 6), 16)
  ];
  
  // Add title with primary color
  doc.setFontSize(16);
  doc.setTextColor(primaryColorRGB[0], primaryColorRGB[1], primaryColorRGB[2]);
  doc.text(`Linked Vehicles for ${lead.customerName || 'Lead'}`, 14, 15);
  
  // Add lead info with primary color
  doc.setFontSize(10);
  doc.setTextColor(primaryColorRGB[0], primaryColorRGB[1], primaryColorRGB[2]);
  doc.text(`Generated on: ${format(new Date(), 'MM/dd/yyyy')}`, 14, 22);
  if (lead.company) {
    doc.text(`Company: ${lead.company}`, 14, 27);
  }
  
  // Prepare table data
  const tableColumn = [
    "Type", 
    "Year/Make/Model", 
    "Stock #",
    "Size", 
    "Mileage", 
    "Condition", 
    "Price", 
    "Status"
  ];
  
  const tableRows = linkedVehicles.map(vehicle => [
    vehicle.type || 'N/A',
    `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() || 'N/A',
    vehicle.stock_number || 'N/A',
    vehicle.size || 'N/A',
    vehicle.miles ? formatDistance(vehicle.miles) : 'N/A',
    vehicle.condition || 'N/A',
    vehicle.optic_list_price ? formatPrice(vehicle.optic_list_price) : 'N/A',
    vehicle.status || 'N/A'
  ]);
  
  // Add the table
  (doc as any).autoTable({
    head: [tableColumn],
    body: tableRows,
    startY: 32,
    styles: { fontSize: 8 },
    headStyles: { 
      fillColor: primaryColorRGB,
      textColor: [255, 255, 255]
    },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    didParseCell: (data: any) => {
      if (data.section === 'body') {
        data.cell.styles.textColor = primaryColorRGB;
      }
    }
  });
  
  if (returnDoc) {
    return doc;
  } else {
    doc.save(`Linked Vehicles for ${lead.customerName}.pdf`);
    return null;
  }
}; 

// Invoice company and wire information interface
// Uses WireInfo from database, with optional phone number
export interface InvoiceCompanyInfo {
  company: string;
  address: string;
  bank: string;
  routingNumber: string;
  accountNumber: string;
  phone?: string; // Optional phone number
}

export interface InvoiceClientInfo {
  firstName?: string;
  lastName?: string;
  company?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface SaleInvoicePdfOptions {
  clientInfo?: InvoiceClientInfo;
  leadNumber?: string | null;
}

export const invoiceClientInfoFromRecipient = (
  recipient?: {
    first_name?: string;
    last_name?: string;
    company?: string;
    street_address?: string;
    city?: string;
    state?: string;
    postal_code?: string;
  } | null,
): InvoiceClientInfo | undefined => {
  if (!recipient) return undefined;
  return {
    firstName: recipient.first_name,
    lastName: recipient.last_name,
    company: recipient.company,
    streetAddress: recipient.street_address,
    city: recipient.city,
    state: recipient.state,
    postalCode: recipient.postal_code,
  };
};

export const formatInvoiceClientLines = (
  client?: InvoiceClientInfo | null,
): string[] => {
  if (!client) return [];

  const name = [client.firstName, client.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const cityState = [client.city, client.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  const cityStateZip = [cityState, client.postalCode?.trim()]
    .filter(Boolean)
    .join(' ');

  return [client.company?.trim(), name, client.streetAddress?.trim(), cityStateZip].filter(
    (line): line is string => !!line,
  );
};

const formatCurrency = (amount: number) =>
  amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatSignedCurrency = (amount: number) =>
  amount < 0 ? `-$${formatCurrency(Math.abs(amount))}` : `$${formatCurrency(amount)}`;

const LEFT_X = 14;
const BILL_TO_X = 118;
const PAGE_RIGHT = 196;

/** Draws company, invoice meta, Bill To, and amount due. Returns table start Y. */
const drawInvoiceDocumentHeader = (
  doc: jsPDF,
  options: {
    title: string;
    companyAddress: string;
    companyPhone?: string;
    invoiceNumber?: string;
    invoiceDate: string;
    leadNumber?: string | null;
    clientInfo?: InvoiceClientInfo;
    amountDueLabel: string;
    amountDueValue: string;
  },
): number => {
  const {
    title,
    companyAddress,
    companyPhone,
    invoiceNumber,
    invoiceDate,
    leadNumber,
    clientInfo,
    amountDueLabel,
    amountDueValue,
  } = options;

  const leftColumnWidth = BILL_TO_X - LEFT_X - 8;
  const rightColumnWidth = PAGE_RIGHT - BILL_TO_X;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  const titleLines = doc.splitTextToSize(title, PAGE_RIGHT - LEFT_X);
  doc.text(titleLines, LEFT_X, 20);
  const titleLineCount = Array.isArray(titleLines) ? titleLines.length : 1;
  let cursorY = 20 + 8 * titleLineCount;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const addressLine = companyPhone ? `${companyAddress} ${companyPhone}` : companyAddress;
  const addressLines = doc.splitTextToSize(addressLine, leftColumnWidth);
  doc.text(addressLines, LEFT_X, cursorY);
  const addressLineCount = Array.isArray(addressLines) ? addressLines.length : 1;

  let leftY = cursorY + 6 * addressLineCount + 2;
  if (invoiceNumber) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Invoice #: ${invoiceNumber}`, LEFT_X, leftY);
    leftY += 8;
    doc.setFont('helvetica', 'normal');
  }
  doc.text(`Invoice Date: ${invoiceDate}`, LEFT_X, leftY);
  leftY += 8;

  const trimmedLeadNumber = leadNumber?.trim();
  if (trimmedLeadNumber) {
    doc.setFont('helvetica', 'bold');
    doc.text(`Lead #: ${trimmedLeadNumber}`, LEFT_X, leftY);
    doc.setFont('helvetica', 'normal');
    leftY += 8;
  }

  const clientLines = formatInvoiceClientLines(clientInfo);
  let rightY = cursorY;
  if (clientLines.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.text('Customer:', BILL_TO_X, rightY);
    rightY += 8;
    doc.setFont('helvetica', 'normal');
    clientLines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, rightColumnWidth);
      doc.text(wrapped, BILL_TO_X, rightY);
      rightY += 7 * (Array.isArray(wrapped) ? wrapped.length : 1);
    });
  }

  const amountDueY = Math.max(leftY, rightY) + 4;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`${amountDueLabel}: ${amountDueValue}`, LEFT_X, amountDueY);

  return amountDueY + 10;
};

// Generate PDF invoice for Retail Orders (matches PandaDoc Retail Order: optic_list_price + shipping per vehicle)
export const generateSaleInvoicePDF = (
  vehicles: Vehicle[],
  returnDoc = false,
  invoiceNumber?: string,
  companyInfo?: InvoiceCompanyInfo,
  /** Per-vehicle shipping (vehicleId -> amount); price per row = optic_list_price + shipping */
  shippingPricesByVehicleId?: Record<string, number>,
  /** Optional per-vehicle list price override (vehicleId -> amount) */
  listPricesByVehicleId?: Record<string, number>,
  /** When true, hides VIN values/column from the invoice table */
  generateWithoutVins = false,
  /** When true, adds the retail order documentation fee per vehicle to the total */
  includeDocFee = false,
  /** Optional order-level charges/deposits included as table rows and in the total */
  extraLineItems?: Array<{ label: string; amount: number; type?: 'charge' | 'deposit' }>,
  options?: SaleInvoicePdfOptions,
) => {
  if (!vehicles.length) return null;
  
  // Create a new PDF document
  const doc = new jsPDF();
  
  // Company information - use provided info or fallback to defaults
  const companyName = companyInfo?.company || 'not found';
  const companyAddress = companyInfo?.address || 'not found';
  const companyPhone = companyInfo?.phone || '';
  
  // Wire instructions - use provided info or fallback to defaults
  const wireCompanyName = companyInfo?.company || 'not found';
  const wireAddress = companyInfo?.address || 'not found';
  const wireBank = companyInfo?.bank || 'not found';
  const wireRouting = companyInfo?.routingNumber || 'not found';
  const wireAccount = companyInfo?.accountNumber || 'not found';

  const validExtraLineItems = (extraLineItems || [])
    .map((item) => ({
      label: item.label?.trim() || '',
      amount: Number(item.amount),
      type: item.type === 'deposit' ? ('deposit' as const) : ('charge' as const),
    }))
    .filter((item) => item.label && Number.isFinite(item.amount) && item.amount > 0);
  const extraLineItemsTotal = validExtraLineItems.reduce((sum, item) => {
    const signed = item.type === 'deposit' ? -item.amount : item.amount;
    return sum + signed;
  }, 0);
  
  // Total = sum of (optic_list_price + shipping) per vehicle, plus doc fee / extra line items when applicable
  const vehiclesSubtotal = vehicles.reduce((sum, vehicle) => {
    const listPrice = listPricesByVehicleId?.[vehicle.id] ?? (vehicle.optic_list_price || 0);
    const shipping = shippingPricesByVehicleId?.[vehicle.id] ?? 0;
    return sum + listPrice + shipping;
  }, 0);
  const docFeeAmount = includeDocFee ? RETAIL_ORDER_DOC_FEE * vehicles.length : 0;
  const totalAmount = vehiclesSubtotal + docFeeAmount + extraLineItemsTotal;
  
  // Format date
  const invoiceDate = format(new Date(), 'MM/dd/yyyy');
  const tableStartY = drawInvoiceDocumentHeader(doc, {
    title: `${companyName} Invoice`,
    companyAddress,
    companyPhone,
    invoiceNumber,
    invoiceDate,
    leadNumber: options?.leadNumber,
    clientInfo: options?.clientInfo,
    amountDueLabel: 'Amount Due',
    amountDueValue: formatSignedCurrency(totalAmount),
  });

  // Table: Year, Make, Model, Miles, [VIN], Price (price = optic_list_price + shipping for that vehicle)
  const tableColumns = generateWithoutVins
    ? ['Year', 'Make', 'Model', 'Miles', 'Price']
    : ['Year', 'Make', 'Model', 'Miles', 'VIN', 'Price'];
  const tableRows = vehicles.map(vehicle => {
    const listPrice = listPricesByVehicleId?.[vehicle.id] ?? (vehicle.optic_list_price || 0);
    const shipping = shippingPricesByVehicleId?.[vehicle.id] ?? 0;
    const price = listPrice + shipping;
    const baseRow = [
      vehicle.year?.toString() || 'N/A',
      vehicle.make || 'N/A',
      vehicle.model || 'N/A',
      vehicle.miles != null ? formatDistance(vehicle.miles) : 'N/A',
      `$${formatCurrency(price)}`,
    ];
    return generateWithoutVins
      ? baseRow
      : [
          baseRow[0],
          baseRow[1],
          baseRow[2],
          baseRow[3],
          vehicle.vin || 'N/A',
          baseRow[4],
        ];
  });

  if (includeDocFee && docFeeAmount > 0) {
    tableRows.push(
      generateWithoutVins
        ? ['', '', 'Documentary Fee', '', `$${formatCurrency(docFeeAmount)}`]
        : ['', '', 'Documentary Fee', '', '', `$${formatCurrency(docFeeAmount)}`]
    );
  }

  validExtraLineItems.forEach((item) => {
    const signedAmount = item.type === 'deposit' ? -item.amount : item.amount;
    tableRows.push(
      generateWithoutVins
        ? ['', '', item.label, '', formatSignedCurrency(signedAmount)]
        : ['', '', item.label, '', '', formatSignedCurrency(signedAmount)]
    );
  });

  (doc as any).autoTable({
    head: [tableColumns],
    body: tableRows,
    startY: tableStartY,
    styles: { fontSize: 10 },
    headStyles: { 
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    columnStyles: generateWithoutVins
      ? {
          0: { cellWidth: 25 },
          1: { cellWidth: 30 },
          2: { cellWidth: 45 },
          3: { cellWidth: 30 },
          4: { cellWidth: 35 },
        }
      : {
          0: { cellWidth: 22 },
          1: { cellWidth: 25 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
          4: { cellWidth: 45 },
          5: { cellWidth: 30 },
        }
  });
  
  // Get the final Y position after the table
  const finalY = (doc as any).lastAutoTable.finalY || 80;
  
  // Add wire instructions section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Wire Instructions', 14, finalY + 15);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(wireCompanyName, 14, finalY + 23);
  doc.text(wireAddress, 14, finalY + 30);
  doc.text(wireBank, 14, finalY + 37);
  doc.text(`Wire Routing: ${wireRouting}`, 14, finalY + 44);
  doc.text(`Account: ${wireAccount}`, 14, finalY + 51);
  
  // Add memo about including invoice number in wire transfer
  if (invoiceNumber) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 0, 0); // Red for memo
    const memoY = finalY + 62;
    const memoText = `MANDATORY: Please include Invoice Number ${invoiceNumber} AND overnight address for titles in the wire transfer memo/reference field when transferring funds.`;
    // Split text if too long to fit on one line (max width ~180mm)
    const maxWidth = 180;
    const splitText = doc.splitTextToSize(memoText, maxWidth);
    doc.text(splitText, 14, memoY);
    doc.setTextColor(0, 0, 0); // Reset to black
  }
  
  if (returnDoc) {
    return doc;
  } else {
    doc.save(`Retail Order - ${format(new Date(), 'MM-dd-yyyy')}.pdf`);
    return null;
  }
};

// Generate PDF listing trucks with a single down payment amount (no per-vehicle prices)
export const generateDownPaymentPDF = (
  vehicles: Vehicle[],
  downPaymentAmount: number,
  returnDoc = false,
  invoiceNumber?: string,
  companyInfo?: InvoiceCompanyInfo,
  hideVins = false,
  options?: SaleInvoicePdfOptions,
) => {
  if (!vehicles.length) return null;

  const doc = new jsPDF();

  const companyName = companyInfo?.company || 'not found';
  const companyAddress = companyInfo?.address || 'not found';
  const companyPhone = companyInfo?.phone || '';

  const wireCompanyName = companyInfo?.company || 'not found';
  const wireAddress = companyInfo?.address || 'not found';
  const wireBank = companyInfo?.bank || 'not found';
  const wireRouting = companyInfo?.routingNumber || 'not found';
  const wireAccount = companyInfo?.accountNumber || 'not found';

  const invoiceDate = format(new Date(), 'MM/dd/yyyy');
  const tableStartY = drawInvoiceDocumentHeader(doc, {
    title: `${companyName} Down Payment`,
    companyAddress,
    companyPhone,
    invoiceNumber,
    invoiceDate,
    leadNumber: options?.leadNumber,
    clientInfo: options?.clientInfo,
    amountDueLabel: 'Down Payment Due',
    amountDueValue: `$${formatCurrency(downPaymentAmount)}`,
  });

  const tableColumns = hideVins
    ? ['Year', 'Make', 'Model', 'Miles']
    : ['Year', 'Make', 'Model', 'Miles', 'VIN'];
  const tableRows = vehicles.map(vehicle => {
    const baseRow = [
      vehicle.year?.toString() || 'N/A',
      vehicle.make || 'N/A',
      vehicle.model || 'N/A',
      vehicle.miles != null ? formatDistance(vehicle.miles) : 'N/A',
    ];
    return hideVins ? baseRow : [...baseRow, vehicle.vin || 'N/A'];
  });

  (doc as any).autoTable({
    head: [tableColumns],
    body: tableRows,
    startY: tableStartY,
    styles: { fontSize: 10 },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    columnStyles: hideVins
      ? {
          0: { cellWidth: 28 },
          1: { cellWidth: 35 },
          2: { cellWidth: 55 },
          3: { cellWidth: 35 },
        }
      : {
          0: { cellWidth: 22 },
          1: { cellWidth: 28 },
          2: { cellWidth: 40 },
          3: { cellWidth: 28 },
          4: { cellWidth: 55 },
        },
  });

  const finalY = (doc as any).lastAutoTable.finalY || 80;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Wire Instructions', 14, finalY + 15);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(wireCompanyName, 14, finalY + 23);
  doc.text(wireAddress, 14, finalY + 30);
  doc.text(wireBank, 14, finalY + 37);
  doc.text(`Wire Routing: ${wireRouting}`, 14, finalY + 44);
  doc.text(`Account: ${wireAccount}`, 14, finalY + 51);

  if (invoiceNumber) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 0, 0);
    const memoY = finalY + 62;
    const memoText = `MANDATORY: Please include Invoice Number ${invoiceNumber} AND overnight address for titles in the wire transfer memo/reference field when transferring funds.`;
    const maxWidth = 180;
    const splitText = doc.splitTextToSize(memoText, maxWidth);
    doc.text(splitText, 14, memoY);
    doc.setTextColor(0, 0, 0);
  }

  if (returnDoc) {
    return doc;
  }
  doc.save(`Down Payment - ${format(new Date(), 'MM-dd-yyyy')}.pdf`);
  return null;
};